import { supabase } from './supabaseClient';
import { getR2WorkerUrl, getR2AuthKey, uploadDocumentToR2 } from './r2Storage';

// ==============================================================================
// HYBRID STORAGE SERVICE
// Description: Dual-tier storage service designed for Dental & CSSD risk evidence
// - Full-resolution storage: On-prem Synology NAS API (High storage capacity, hospital intranet)
// - Fast preview thumbnails: Cloudflare R2 / Supabase Storage / Base64 Data URL
// ==============================================================================

export const SYNOLOGY_API_URL =
  (typeof process !== 'undefined' && (process.env.REACT_APP_SYNOLOGY_NAS_URL || process.env.VITE_SYNOLOGY_NAS_URL)) ||
  'https://risk-api.dentfang.com/upload';

/**
 * Generate a client-side thumbnail blob using HTML5 Canvas.
 * Formats: image/webp with fallback to image/jpeg.
 *
 * @param {File|Blob} file - The source image file
 * @param {number} [maxWidth=300] - Maximum width constraint
 * @param {number} [maxHeight=300] - Maximum height constraint
 * @param {number} [quality=0.75] - Compression quality (0.0 to 1.0)
 * @returns {Promise<Blob|null>} - Resolves to the thumbnail Blob or null
 */
export const generateClientThumbnail = (file, maxWidth = 300, maxHeight = 300, quality = 0.75) => {
  return new Promise((resolve) => {
    // Graceful fallback for non-browser or missing canvas support
    if (
      typeof window === 'undefined' ||
      !window.document ||
      !window.HTMLCanvasElement ||
      !window.Image ||
      !window.URL ||
      !file
    ) {
      return resolve(null);
    }

    if (!file.type || !file.type.startsWith('image/')) {
      return resolve(null);
    }

    let objectUrl = '';
    try {
      objectUrl = window.URL.createObjectURL(file);
    } catch (e) {
      return resolve(null);
    }

    const img = new window.Image();

    img.onload = () => {
      try {
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        if (width <= 0 || height <= 0) {
          window.URL.revokeObjectURL(objectUrl);
          return resolve(null);
        }

        // Calculate aspect-ratio preserved dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        width = Math.max(1, width);
        height = Math.max(1, height);

        const canvas = window.document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          window.URL.revokeObjectURL(objectUrl);
          return resolve(null);
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Attempt WebP format first
        canvas.toBlob(
          (webpBlob) => {
            window.URL.revokeObjectURL(objectUrl);
            if (webpBlob && webpBlob.size > 0) {
              resolve(webpBlob);
            } else {
              // Fallback to JPEG
              canvas.toBlob(
                (jpegBlob) => {
                  resolve(jpegBlob || null);
                },
                'image/jpeg',
                quality
              );
            }
          },
          'image/webp',
          quality
        );
      } catch (err) {
        window.URL.revokeObjectURL(objectUrl);
        console.warn('[HybridStorage] Canvas processing error:', err);
        resolve(null);
      }
    };

    img.onerror = () => {
      window.URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });
};

/**
 * Upload high-resolution evidence file to Synology NAS API.
 * Uses multipart/form-data with timeout and error handling.
 *
 * @param {File|Blob} file - The file to upload
 * @returns {Promise<{ success: boolean, url: string|null, filename: string, error?: string }>}
 */
export const uploadToSynologyNasApi = async (file) => {
  if (!file) {
    return { success: false, url: null, filename: '', error: 'No file provided' };
  }

  const filename = file.name || `evidence_${Date.now()}`;
  const formData = new FormData();
  formData.append('file', file, filename);

  // Set 15-second abort controller timeout
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), 15000) : null;

  try {
    const response = await fetch(SYNOLOGY_API_URL, {
      method: 'POST',
      body: formData,
      signal: controller ? controller.signal : undefined
    });

    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.warn(`[HybridStorage] Synology NAS API returned HTTP ${response.status}: ${errorText}`);
      return {
        success: false,
        url: null,
        filename,
        error: `HTTP ${response.status}: ${errorText || response.statusText}`
      };
    }

    const data = await response.json().catch(() => ({}));
    const fullUrl = data.url || data.file_url || data.path || null;
    const resFilename = data.filename || data.fileName || filename;

    if (fullUrl) {
      return {
        success: true,
        url: fullUrl,
        filename: resFilename
      };
    }

    return {
      success: false,
      url: null,
      filename,
      error: (data && data.error) || 'Invalid response format from Synology API'
    };
  } catch (err) {
    if (timeoutId) clearTimeout(timeoutId);
    const isTimeout = err.name === 'AbortError';
    console.warn('[HybridStorage] Synology NAS API upload error:', isTimeout ? 'Timed out after 15s' : err.message);
    return {
      success: false,
      url: null,
      filename,
      error: isTimeout ? 'Request timed out after 15s' : (err.message || 'Network error')
    };
  }
};

/**
 * Upload thumbnail to Cloudflare R2 / Supabase Storage with fallback to Base64 Data URL.
 *
 * @param {Blob} thumbBlob - The thumbnail image blob
 * @param {string} [filename] - The desired filename
 * @returns {Promise<{ success: boolean, url: string|null, storage: string }>}
 */
export const uploadToCloudflareR2 = async (thumbBlob, filename) => {
  if (!thumbBlob) {
    return { success: false, url: null, storage: 'none' };
  }

  const safeFilename = (filename || `thumb_${Date.now()}.webp`).replace(/[^a-zA-Z0-9._-]/g, '_');

  // Tier 1: Cloudflare R2 Worker
  try {
    const workerUrl = typeof getR2WorkerUrl === 'function' ? getR2WorkerUrl() : '';
    if (workerUrl && typeof workerUrl === 'string' && workerUrl.trim() !== '') {
      const cleanUrl = workerUrl.trim().replace(/\/+$/, '');
      const endpoint = `${cleanUrl}/upload?folder=risk_thumbnails`;

      const formData = new FormData();
      formData.append('file', thumbBlob, safeFilename);

      const headers = {};
      const authKey = typeof getR2AuthKey === 'function' ? getR2AuthKey() : '';
      if (authKey) {
        headers['X-Custom-Auth-Key'] = authKey;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.success && data.url) {
          return { success: true, url: data.url, storage: 'cloudflare_r2' };
        }
      }
    }
  } catch (r2Err) {
    console.warn('[HybridStorage] Cloudflare R2 thumbnail upload failed, trying Supabase Storage:', r2Err);
  }

  // Tier 2: Supabase Storage
  if (supabase && supabase.storage) {
    try {
      const bucketName = 'risk-evidence';
      const filePath = `thumbnails/${Date.now()}_${safeFilename}`;
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, thumbBlob, {
          contentType: thumbBlob.type || 'image/webp',
          upsert: true
        });

      if (!error && data) {
        const { data: urlData } = supabase.storage.from(bucketName).getPublicUrl(filePath);
        if (urlData && urlData.publicUrl) {
          return { success: true, url: urlData.publicUrl, storage: 'supabase_storage' };
        }
      }
    } catch (sbErr) {
      console.warn('[HybridStorage] Supabase Storage thumbnail upload failed, trying Data URL fallback:', sbErr);
    }
  }

  // Tier 3: Fallback to Base64 Data URL
  try {
    const base64DataUrl = await new Promise((resolve) => {
      if (typeof window === 'undefined' || typeof window.FileReader === 'undefined') {
        return resolve(null);
      }
      const reader = new window.FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(thumbBlob);
    });

    if (base64DataUrl) {
      return {
        success: true,
        url: base64DataUrl,
        storage: 'base64_data_url'
      };
    }
  } catch (fallbackErr) {
    console.warn('[HybridStorage] Base64 thumbnail generation failed:', fallbackErr);
  }

  return { success: false, url: null, storage: 'failed' };
};

/**
 * Orchestrate the dual-tier evidence upload process:
 * 1. Generates a client-side thumbnail (WebP/JPEG)
 * 2. Uploads full high-res file to on-prem Synology NAS API
 * 3. Uploads thumbnail to Cloudflare R2 / Supabase Storage / Data URL
 * 4. Provides zero-data-loss fallback if Synology NAS is currently unreachable
 *
 * @param {File|Blob} file - Original evidence file
 * @returns {Promise<{
 *   id: string,
 *   filename: string,
 *   full_url: string|null,
 *   thumb_url: string|null,
 *   size_full_bytes: number,
 *   size_thumb_bytes: number,
 *   mime_type: string,
 *   uploaded_at: string,
 *   storage_backend: string
 * }>}
 */
export const uploadRiskEvidence = async (file) => {
  if (!file) {
    throw new Error('No file provided for upload');
  }

  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `ev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  const filename = file.name || `evidence_${id}`;
  const mimeType = file.type || 'application/octet-stream';
  const sizeFullBytes = file.size || 0;
  const isImage = mimeType.startsWith('image/');

  // 1. Generate client-side thumbnail for images
  let thumbBlob = null;
  let sizeThumbBytes = 0;
  if (isImage) {
    try {
      thumbBlob = await generateClientThumbnail(file, 300, 300, 0.75);
      if (thumbBlob) {
        sizeThumbBytes = thumbBlob.size || 0;
      }
    } catch (thumbErr) {
      console.warn('[HybridStorage] Thumbnail generation warning:', thumbErr);
    }
  }

  // 2. Parallel upload execution
  const [synologyResult, r2Result] = await Promise.all([
    uploadToSynologyNasApi(file),
    thumbBlob ? uploadToCloudflareR2(thumbBlob, `thumb_${filename}`) : Promise.resolve(null)
  ]);

  let fullUrl = synologyResult?.success ? synologyResult.url : null;
  let thumbUrl = r2Result?.success ? r2Result.url : null;
  let backend = synologyResult?.success ? 'synology_nas' : 'none';

  if (r2Result?.success) {
    backend = synologyResult?.success
      ? `synology+${r2Result.storage}`
      : r2Result.storage;
  }

  // 3. Fallback: If Synology NAS is offline/unreachable, route full document to R2/Cloudinary
  if (!fullUrl) {
    console.warn('[HybridStorage] Primary Synology NAS upload failed; attempting secondary fallback for full document.');
    try {
      if (typeof uploadDocumentToR2 === 'function') {
        fullUrl = await uploadDocumentToR2(file, 'risk_evidence');
        backend = backend === 'none' ? 'cloudflare_r2_fallback' : `${backend}+r2_full`;
      }
    } catch (r2FallbackErr) {
      console.warn('[HybridStorage] Secondary R2 upload failed:', r2FallbackErr);
    }

    if (!fullUrl && thumbUrl) {
      fullUrl = thumbUrl;
    }
  }

  // Fallback for missing thumbnail
  if (!thumbUrl && fullUrl) {
    thumbUrl = fullUrl;
  }

  return {
    id,
    filename,
    full_url: fullUrl,
    thumb_url: thumbUrl,
    size_full_bytes: sizeFullBytes,
    size_thumb_bytes: sizeThumbBytes,
    mime_type: mimeType,
    uploaded_at: new Date().toISOString(),
    storage_backend: backend
  };
};

const hybridStorageService = {
  generateClientThumbnail,
  uploadToSynologyNasApi,
  uploadToCloudflareR2,
  uploadRiskEvidence
};

export default hybridStorageService;
